// BiffReader.ts - BIFF8 record-level parser
//
// Parses the binary record stream from an Excel BIFF8 workbook stream.
// Each record is: [type: uint16LE] [length: uint16LE] [data: length bytes]
//
// Reference: [MS-XLS] https://docs.microsoft.com/en-us/openspecs/office_file_formats/ms-xls

// ---- BIFF8 Record Type Constants ----

export const BIFF = {
  BOF:            0x0809,
  EOF:            0x000a,
  BOUNDSHEET:     0x0085,
  SST:            0x00fc,
  CONTINUE:       0x003c,
  LABELSST:       0x00fd,
  LABEL:          0x0204,
  NUMBER:         0x0203,
  RK:             0x027e,
  MULRK:          0x00bd,
  MULBLANK:       0x00be,
  BLANK:          0x0201,
  BOOLERR:        0x0205,
  FORMULA:        0x0006,
  STRING:         0x0207,
  RSTRING:        0x00d6,
  XF:             0x00e0,
  FONT:           0x0031,
  FORMAT:         0x041e,
  PALETTE:        0x0092,
  DIMENSION:      0x0200,
  ROW:            0x0208,
  INDEX:          0x020b,
  DBCELL:         0x00d7,
  WINDOW2:        0x023e,
  MERGEDCELLS:    0x00e5,
  DATEMODE:       0x0022,
  CODEPAGE:       0x0042,
  FILEPASS:       0x002f,
} as const;

/** A single BIFF record. */
export interface BiffRecord {
  type: number;
  data: Uint8Array;
}

/**
 * Iterate over BIFF records in a stream buffer.
 * Automatically coalesces CONTINUE records into the preceding record.
 */
export function* iterateBiffRecords(buf: Uint8Array): Generator<BiffRecord> {
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  let pos = 0;
  let pending: BiffRecord | null = null;

  while (pos + 4 <= buf.length) {
    const type = view.getUint16(pos, true);
    const len = view.getUint16(pos + 2, true);
    pos += 4;

    if (pos + len > buf.length) break; // truncated record

    const data = buf.subarray(pos, pos + len);
    pos += len;

    if (type === BIFF.CONTINUE && pending) {
      // Coalesce CONTINUE into the previous record
      const merged: Uint8Array = new Uint8Array(pending.data.length + data.length);
      merged.set(pending.data, 0);
      merged.set(data, pending.data.length);
      pending = { type: pending.type, data: merged };
    } else {
      if (pending) yield pending;
      pending = { type, data };
    }
  }

  if (pending) yield pending;
}

// ---- Low-level decode helpers ----

/**
 * Decode an RK value to a number.
 * RK values encode integers and IEEE doubles in a compact 4-byte format.
 */
export function decodeRkValue(rk: number): number {
  const isDiv100 = (rk & 1) !== 0;
  const isInt = (rk & 2) !== 0;

  let value: number;
  if (isInt) {
    // Signed 30-bit integer (arithmetic shift right 2)
    value = rk >> 2;
  } else {
    // IEEE 754 double with low 32 bits zeroed and top 2 bits replaced
    const buf = new ArrayBuffer(8);
    const dv = new DataView(buf);
    dv.setUint32(0, rk & 0xfffffffc, true); // low 4 bytes with bottom 2 bits cleared
    dv.setUint32(4, 0, true); // not needed, but clarifies
    // Actually for RK the encoding puts the 30 useful bits in the HIGH doubleword
    dv.setUint32(4, rk & 0xfffffffc, false);
    dv.setUint32(0, 0, false);
    value = dv.getFloat64(0, false);
  }

  return isDiv100 ? value / 100 : value;
}

/**
 * Decode a BIFF8 unicode string from a buffer.
 *
 * BIFF8 strings: [charCount: uint16] [flags: uint8] [chars...]
 * Flag bit 0: 0 = compressed (Latin-1, 1 byte/char), 1 = uncompressed (UTF-16LE, 2 bytes/char)
 * Flag bit 2: 1 = rich text (has run count)
 * Flag bit 3: 1 = East Asian phonetic (has extra byte count)
 */
export function decodeBiffString(
  buf: Uint8Array,
  offset: number,
): { text: string; bytesConsumed: number } {
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  let pos = offset;

  const charCount = view.getUint16(pos, true);
  pos += 2;
  const flags = buf[pos];
  pos += 1;

  const isWide = (flags & 0x01) !== 0;
  const hasRichText = (flags & 0x08) !== 0;
  const hasPhonetic = (flags & 0x04) !== 0;

  let richRunCount = 0;
  if (hasRichText) {
    richRunCount = view.getUint16(pos, true);
    pos += 2;
  }
  let phoneticSize = 0;
  if (hasPhonetic) {
    phoneticSize = view.getInt32(pos, true);
    pos += 4;
  }

  let text = '';
  if (isWide) {
    for (let i = 0; i < charCount; i++) {
      text += String.fromCharCode(view.getUint16(pos, true));
      pos += 2;
    }
  } else {
    for (let i = 0; i < charCount; i++) {
      text += String.fromCharCode(buf[pos]);
      pos += 1;
    }
  }

  // Skip rich text runs and phonetic data
  pos += richRunCount * 4;
  pos += phoneticSize;

  return { text, bytesConsumed: pos - offset };
}

/**
 * Decode a short BIFF8 string (1-byte length prefix instead of 2-byte).
 * Used in BOUNDSHEET and other records that use ShortXLUnicodeString.
 */
export function decodeBiffShortString(
  buf: Uint8Array,
  offset: number,
): { text: string; bytesConsumed: number } {
  let pos = offset;
  const charCount = buf[pos];
  pos += 1;
  const flags = buf[pos];
  pos += 1;

  const isWide = (flags & 0x01) !== 0;

  let text = '';
  if (isWide) {
    for (let i = 0; i < charCount; i++) {
      text += String.fromCharCode(new DataView(buf.buffer, buf.byteOffset).getUint16(pos, true));
      pos += 2;
    }
  } else {
    for (let i = 0; i < charCount; i++) {
      text += String.fromCharCode(buf[i + pos]);
      pos += 1;
    }
  }

  return { text, bytesConsumed: pos - offset };
}
